import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule'
import Webhook from '@/models/Settings/Webhooks/Webhook';
import CreateWebhookRequest from '@/models/Settings/Webhooks/CreateWebhookRequest';
import UpdateWebhookRequest from '@/models/Settings/Webhooks/UpdateWebhookRequest';
import PermissionUtils from '@/lib/permissionUtils';

export interface IWebhooksState {
    loading: boolean;
    loadingWebhooks: boolean;
    loadingDialog: boolean;
    webhookEvents: string[];
    webhooks: Webhook[];
    totalWebhooksPages: number;
    webhook: Webhook;
    newWebhookId: string;
    publicKey: string;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'WebhooksModule', namespaced: true })
class WebhooksStore extends VuexModule implements IWebhooksState {

    public loading: boolean = true;
    public loadingWebhooks: boolean = true;
    public loadingDialog: boolean = false;
    public webhookEvents: string[] = [];
    public webhook: Webhook = new Webhook();
    public webhooks: Webhook[] = [];
    public totalWebhooksPages: number = 1;
    public newWebhookId: string = null;
    public publicKey: string = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async createWebhook(request: CreateWebhookRequest) {

        this.setLoading(true);
        this.context.commit("setWebhookFromCreate", null);

        await ajax.post(`/portal-backend/fi-api/webhooks`, request)
        .then(response => {
            this.context.commit("setWebhookFromCreate", response.data.id);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async editWebhook(request: UpdateWebhookRequest) {

        this.setLoadingDialog(true);

        await ajax.patch(`/portal-backend/fi-api/webhooks/${this.webhook.id}`, request)
        .then(response => {
            this.context.commit("setWebhook", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getWebhookEvents() {
        await ajax.get('/portal-backend/fi-api/webhook-events')
        .then(response => {
            if (response.data.results === null){
                response.data.results = [];
            }
            this.context.commit("setWebhookEvents", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getWebhooks(pageNumber: number) {

        this.setLoadingWebhooks(true);

        await ajax.get(`/portal-backend/fi-api/webhooks?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setWebhooks", response.data.webhooks);
            this.context.commit("setWebhooksTotalPages", response.data.records.totalRecords);
            this.setLoadingWebhooks(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingWebhooks(false);
        });
    }

    @Action
    async getWebhook(id: string) {

        this.setLoading(true);

        await ajax.get('/portal-backend/fi-api/webhooks/' + id)
        .then(response => {
            this.context.commit("setWebhook", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async deleteWebhook() {

        this.setLoading(true);

        await ajax.delete(`/portal-backend/fi-api/webhooks/${this.webhook.id}`)
        .then(() => {
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getPublicKey() {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/webhooks-public-key`)
        .then(response => {
            this.context.commit("setPublicKey", response.data.publicKey);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingWebhooks = true;
        this.loadingDialog = false;
        this.webhookEvents = [];
        this.webhooks = [];
        this.webhook = new Webhook();
        this.totalWebhooksPages = 1;
        this.newWebhookId = null;
        this.publicKey = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingWebhooks(loading: boolean) {
        this.loadingWebhooks = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setWebhookEvents(response: string[]) {
        if (response) {
            this.webhookEvents = response;
        }
    }

    @Mutation
    private async setWebhooks(response: Webhook[]) {
        if (response) {
            this.webhooks = response;
        }
    }

    @Mutation
    private async setWebhooksTotalPages(total: number) {
        this.totalWebhooksPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setWebhook(response: Webhook) {
        this.webhook = response;
    }

    @Mutation
    private async setWebhookFromCreate(webhookId: string) {
        this.newWebhookId = webhookId;
    }

    @Mutation
    private async setPublicKey(publicKey: string) {
        this.publicKey = atob(publicKey);
    }

}

export const WebhooksModule = getModule(WebhooksStore);